// CalendarPicker.tsx — Kalender-Dropdown des Kalender-Neubaus (`/termine`, SP5, T-69).
// RdPopover mit drei Abschnitten aus der KALENDER-Liste (GET /calendar/mailboxes
// → MailContext.calendarMailboxes):
//   „Meine Kalender“  — eigenes Postfach + Funktionspostfächer mit Grant (cbb@ …),
//   „Kollegen“        — nur die GEWÄHLTEN Kollegen; „Weitere Kollegen …“ öffnet eine
//                        zweite Ebene mit Suche über alle Kollegen, deren Kalender
//                        Outlook mir freigibt (Server entscheidet),
//   „Raumkalender“    — nur die GEWÄHLTEN Räume; „Raum hinzufügen …“ öffnet eine
//                        zweite Ebene mit Suche über alle Räume (Liste sonst zu lang).
// (Ab-)Wählen schreibt in MailContext (selectedCalendars/toggleCalendar — single
// source of truth, geräteübergreifend gespeichert), der Context lädt nach.

import React, { useMemo, useState } from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import { RdPopover } from "../redesign/table/RdPopover";
import { useMailContext } from "../../context/MailContext";
import {
    anzahlGewaehlt,
    filterEintraege,
    gruppiereQuellen,
    istGewaehlt,
    nurGewaehlte,
    type KalenderEintrag,
} from "./kalenderPickerGruppen";

const CalendarPicker: React.FC = () => {
    const {
        mailboxes,
        calendarMailboxes,
        currentMailbox,
        ownMailbox,
        selectedCalendars,
        toggleCalendar,
        calendarColor,
    } = useMailContext();
    const [ebene, setEbene] = useState<"haupt" | "raeume" | "kollegen">("haupt");
    const [suche, setSuche] = useState("");

    // Kalender-Liste; solange sie fehlt, die Mail-Liste (nur Grants) als Rückfall.
    const gruppen = useMemo(
        () =>
            gruppiereQuellen(
                calendarMailboxes && calendarMailboxes.length > 0 ? calendarMailboxes : mailboxes,
                ownMailbox ?? currentMailbox,
            ),
        [calendarMailboxes, mailboxes, ownMailbox, currentMailbox],
    );
    const gewaehlteRaeume = nurGewaehlte(gruppen.raeume, selectedCalendars);
    const gewaehlteKollegen = nurGewaehlte(gruppen.kollegen, selectedCalendars);
    const unterliste = ebene === "kollegen" ? gruppen.kollegen : gruppen.raeume;
    const gefunden = filterEintraege(unterliste, suche);
    const unterebene = (ziel: "raeume" | "kollegen") => {
        setSuche("");
        setEbene(ziel);
    };
    const selectedCount = anzahlGewaehlt(gruppen, selectedCalendars);
    const leer = gruppen.eigene.length + gruppen.kollegen.length + gruppen.raeume.length === 0;

    const zeile = (it: KalenderEintrag) => (
        <label key={it.address} style={rowStyle}>
            <input
                type="checkbox"
                checked={istGewaehlt(it.address, selectedCalendars)}
                onChange={() => toggleCalendar(it.address)}
                style={{ width: 16, height: 16, accentColor: REFINED.aub }}
            />
            <span
                aria-hidden
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: calendarColor(it.address),
                    flex: "0 0 auto",
                }}
            />
            <span style={nameStyle} title={it.address}>
                <span style={ellipsisStyle}>{it.displayName}</span>
                {it.isOwn && <span style={ownTagStyle}>eigen</span>}
                {it.stufeHinweis && <span style={stufeStyle}>{it.stufeHinweis}</span>}
            </span>
        </label>
    );

    return (
        <RdPopover
            width={300}
            align="end"
            trigger={({ toggle, open, ref }) => (
                <button
                    ref={ref}
                    type="button"
                    onClick={() => {
                        if (!open) {
                            setEbene("haupt");
                            setSuche("");
                        }
                        toggle();
                    }}
                    aria-expanded={open}
                    title="Kalender auswählen"
                    style={triggerStyle(open)}
                >
                    <span>▾ Kalender</span>
                    {selectedCount > 0 && <span style={badgeStyle}>{selectedCount}</span>}
                </button>
            )}
        >
            {() =>
                ebene !== "haupt" ? (
                    <div style={panelStyle}>
                        <button type="button" style={backStyle} onClick={() => setEbene("haupt")}>
                            ‹ Zurück zur Kalenderauswahl
                        </button>
                        <div style={headingStyle}>
                            {ebene === "kollegen" ? "Kollegenkalender wählen" : "Raumkalender wählen"}
                        </div>
                        <input
                            type="search"
                            autoFocus
                            value={suche}
                            onChange={(e) => setSuche(e.target.value)}
                            placeholder={ebene === "kollegen" ? "Kollegin/Kollegen suchen …" : "Raum suchen …"}
                            aria-label={ebene === "kollegen" ? "Kollegen suchen" : "Raum suchen"}
                            style={searchStyle}
                        />
                        {gefunden.length === 0 ? (
                            <div style={emptyStyle}>
                                {unterliste.length === 0
                                    ? ebene === "kollegen"
                                        ? "Keine Kollegenkalender freigegeben."
                                        : "Keine Raumkalender verfügbar."
                                    : "Nichts gefunden."}
                            </div>
                        ) : (
                            gefunden.map(zeile)
                        )}
                    </div>
                ) : (
                    <div style={panelStyle}>
                        {leer ? (
                            <div style={emptyStyle}>Keine lesbaren Kalender.</div>
                        ) : (
                            <>
                                {gruppen.eigene.length > 0 && (
                                    <>
                                        <div style={headingStyle}>Meine Kalender</div>
                                        {gruppen.eigene.map(zeile)}
                                    </>
                                )}
                                {gruppen.kollegen.length > 0 && (
                                    <>
                                        <div style={{ ...headingStyle, marginTop: 6 }}>Kollegen</div>
                                        {gewaehlteKollegen.map(zeile)}
                                        <button
                                            type="button"
                                            style={addRoomStyle}
                                            onClick={() => unterebene("kollegen")}
                                        >
                                            + Weitere Kollegen …
                                        </button>
                                    </>
                                )}
                                {gruppen.raeume.length > 0 && (
                                    <>
                                        <div style={{ ...headingStyle, marginTop: 6 }}>Raumkalender</div>
                                        {gewaehlteRaeume.map(zeile)}
                                        <button
                                            type="button"
                                            style={addRoomStyle}
                                            onClick={() => unterebene("raeume")}
                                        >
                                            + Raum hinzufügen …
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )
            }
        </RdPopover>
    );
};

const triggerStyle = (open: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 8,
    border: `1px solid ${open ? REFINED.aub : REFINED.line}`,
    background: REFINED.white,
    color: REFINED.ink,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: "pointer",
    marginLeft: "auto",
    boxShadow: open ? REFINED.focusRing : "none",
});

const badgeStyle: React.CSSProperties = {
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: REFINED.aub,
    color: REFINED.white,
    font: `700 11px ${REFINED.fontUI}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
    background: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    boxShadow: REFINED.cardShadow,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxHeight: 360,
    overflowY: "auto",
};

const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "0 8px",
    borderRadius: 8,
    cursor: "pointer",
    color: REFINED.ink,
    font: `500 14px ${REFINED.fontUI}`,
};

const nameStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 6,
};

const ellipsisStyle: React.CSSProperties = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const headingStyle: React.CSSProperties = {
    padding: "4px 8px",
    color: REFINED.ink3,
    font: `700 11px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.4,
};

const stufeStyle: React.CSSProperties = {
    flex: "0 0 auto",
    color: REFINED.ink3,
    font: `400 11px ${REFINED.fontUI}`,
};

const addRoomStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    minHeight: 40,
    padding: "0 8px",
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: REFINED.aub,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: "pointer",
    textAlign: "left",
};

const backStyle: React.CSSProperties = {
    ...addRoomStyle,
    minHeight: 36,
    font: `600 13px ${REFINED.fontUI}`,
};

const searchStyle: React.CSSProperties = {
    margin: "4px 8px 6px",
    minHeight: 36,
    padding: "0 10px",
    borderRadius: 8,
    border: `1px solid ${REFINED.line}`,
    color: REFINED.ink,
    font: `400 14px ${REFINED.fontUI}`,
};

const ownTagStyle: React.CSSProperties = {
    padding: "1px 6px",
    borderRadius: 6,
    background: REFINED.aub100,
    color: REFINED.aub700,
    font: `700 10px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.3,
};

const emptyStyle: React.CSSProperties = {
    padding: "10px 8px",
    color: REFINED.ink3,
    font: `400 13px ${REFINED.fontUI}`,
};

export default CalendarPicker;
